import { v4 as uuidv4 } from 'uuid';

import { IPmResultDecision } from 'APIPackage/KnmModule/interfaces/IPmResultDecision';
import { convertInitialKnmDateToFormDate } from 'KnmPackage/MainModule/common/convertInitialKnmDateToFormDate';
import {
  DATE_FORMAT_ISO_FNS,
  DATE_FORMAT_LONG_FNS,
  DATE_FORMAT_LONG_MINUTE_FNS,
  SYSTEM_DATE_FORMAT_FNS,
} from 'CorePackage/MainModule/constants/dateFormats';
import { createKindResultDecisionSelectOption } from 'CorePackage/CatalogsModule/common/createKindResultDecisionSelectOption';
import { getSelectOption } from 'KnmErknmPackage/MainModule/common/getSelectOption';
import { DictionaryUUIDsEnum } from 'CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { convertSubjectDataToFormValues } from 'KnmPackage/KnmSubjectModule/common/convertSubjectDataToformValues';
import { convertKnmDataViolatedRequirementsToFormValues } from 'KnmErknmPackage/MainModule/common/convertKnmDataViolatedRequirementsToFormValues';
import { IKnmFormSubject } from 'KnmPackage/KnmSubjectModule/interfaces/IKnmFormSubject';
import { IKnmActFamiliarizationNotificationsForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmActFamiliarizationNotificationsForm';
import { createKnmNotificationMethodSelectOption } from 'CorePackage/CatalogsModule/common/createKnmNotificationMethodSelectOption';

import { IKnmPmFormResultDecision, IKnmPmSubjectTypeSelectOption } from '../interfaces/IKnmPmFormResultDecision';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { convertSignersToFormValues } from '../../../KnmErknmPackage/MainModule/common/convertSignersToFormValues';

interface IProps {
  pmResultDecisions?: IPmResultDecision[] | null;
  state: IRootState;
  resultDecisionSubjectTypeOptions: IKnmPmSubjectTypeSelectOption<string | number>[];
  subject: IKnmFormSubject;
}

export const convertPmResultDecisionDataToPmForm = ({
  pmResultDecisions,
  state,
  resultDecisionSubjectTypeOptions,
  subject,
}: IProps): IKnmPmFormResultDecision[] | undefined => {
  return pmResultDecisions?.map(item => ({
    isNew: !!item.isNew,
    listId: item.guid || uuidv4(),
    dateCreate: item.dateCreate || undefined,
    guid: item.guid || undefined,
    status: item.status || undefined,
    agreementStatus: item.agreementStatus || undefined,
    number: item.number || undefined,
    date: convertInitialKnmDateToFormDate(item.date, DATE_FORMAT_LONG_FNS, DATE_FORMAT_ISO_FNS),
    dateResolvedByAgreement: convertInitialKnmDateToFormDate(
      item.dateResolvedByAgreement,
      DATE_FORMAT_LONG_FNS,
      DATE_FORMAT_ISO_FNS,
    ),
    deadline: convertInitialKnmDateToFormDate(item?.deadline, DATE_FORMAT_LONG_FNS),
    information: item.information || undefined,
    informationReactQuillEditor: handleReactQuillDeltaValue(item.informationReactQuillEditor || item.information),
    signerFullName: item.fioSigner || undefined,
    kindDecision: item.kindDecision ? createKindResultDecisionSelectOption(item.kindDecision) : undefined,
    signerPosition: item.titleSigner?.dictId
      ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
          state,
          item.titleSigner?.dictId,
          item.titleSigner?.dictVersionId,
        )
      : undefined,
    subjectType: resultDecisionSubjectTypeOptions.find(option => option.id === item.subjectType),
    attachment: item.attachment || undefined,
    founder: convertSubjectDataToFormValues(item.founder),
    violatedMandatoryRequirements: item.violations
      ? convertKnmDataViolatedRequirementsToFormValues(item.violations)
      : undefined,
    organization: subject,
    signed: item.signed || undefined,
    paperless: item.paperless || undefined,
    extractVersion: item.extractVersion || undefined,
    familiarizationNotifications:
      item.familiarizationNotifications?.map(x => {
        const notification: IKnmActFamiliarizationNotificationsForm = {
          noticeDate: convertInitialKnmDateToFormDate(x.noticeDate, DATE_FORMAT_LONG_FNS, SYSTEM_DATE_FORMAT_FNS),
          knmNoticeType: x.knmNoticeType ? createKnmNotificationMethodSelectOption(x.knmNoticeType) : undefined,
          note: x.note,
          listId: x.guid || uuidv4(),
          isNew: !!x.isNew,
          existedAtSigning: x.existedAtSigning || undefined,
        };

        return notification;
      }) || undefined,
    firstExtractDateTime:
      convertInitialKnmDateToFormDate(item.firstExtractDateTime, DATE_FORMAT_LONG_MINUTE_FNS, DATE_FORMAT_ISO_FNS) ||
      null,
    systemSignatureUsed: item.systemSignatureUsed || false,
    systemSignatureDateTime: item.systemSignatureDateTime || null,
    systemSignatureMnemonic: item.systemSignatureMnemonic || null,
    systemSignatureTitle: item.systemSignatureTitle || null,
    cancelFioSigner: item.cancelFioSigner || undefined,
    cancelTitleSigner: item.cancelTitleSigner?.dictId
      ? getSelectOption<DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
          state,
          item.cancelTitleSigner?.dictId,
          item.cancelTitleSigner?.dictVersionId,
        )
      : undefined,
    cancelComment: item.cancelComment || undefined,
    cancelDocument: item.cancelDocument || undefined,
    isCancelSigned: item.isCancelSigned || undefined,
    actGuid: item.actGuid || undefined,
    dateAdded: item.dateAdded || undefined,
    signers: convertSignersToFormValues(item.signers, state, undefined),
  }));
};
